import { useCallback } from "react"
import { services } from "@/services"
import { useResource } from "@/shared/hooks/useResource"
import { EmptyState } from "@/shared/ui/EmptyState"
import { Section } from "@/shared/ui/Section"
import { SectionHeading } from "@/shared/ui/SectionHeading"
import { MapPinLg } from "@/shared/ui/icons"
import { CoverageMap } from "@/features/network/components/CoverageMap"
import { NetworkSearchForm } from "@/features/network/components/NetworkSearchForm"
import { NetworkUnitCard } from "@/features/network/components/NetworkUnitCard"
import { useNetworkSearch } from "@/features/network/useNetworkSearch"

function loadCoveredStates() {
  return services.network.listCoveredStates()
}

/** Resumo textual do resultado, com plural correto e os filtros aplicados. */
function ResultSummary({ count, state, city }: { count: number; state: string; city: string }) {
  if (count === 0) return <>Nenhuma unidade encontrada para essa região</>

  const plural = count > 1

  return (
    <>
      <strong className="text-verde-profundo">
        {count} unidade{plural ? "s" : ""}
      </strong>{" "}
      encontrada{plural ? "s" : ""}
      {state ? ` — ${state}` : ""}
      {city ? ` · ${city}` : ""}
    </>
  )
}

export function NetworkSection() {
  const search = useNetworkSearch()
  const coveredStatesLoader = useCallback(loadCoveredStates, [])
  const coveredStates = useResource<string[]>(coveredStatesLoader)

  const units = search.results.data ?? []
  const hasSearched = search.query !== null

  return (
    <Section id="rede" tone="white">
      <SectionHeading
        className="mb-12 max-w-xl"
        eyebrow="Rede credenciada"
        title="Encontre atendimento perto de você"
        description="Consulte as unidades da rede credenciada DIMPET por CEP, estado ou cidade."
      />

      <div className="grid lg:grid-cols-[1fr_380px] gap-10 lg:gap-14 items-start">
        {/* ── Busca e resultados ──────────────────────────────────────────── */}
        <div>
          <NetworkSearchForm
            filters={search.filters}
            onChange={search.setFilters}
            onSubmit={search.search}
            loading={hasSearched && search.results.loading}
            error={search.formError}
          />

          <div ref={search.resultsRef}>
            {!hasSearched ? (
              <EmptyState icon={<MapPinLg />}>
                Informe seu CEP ou selecione estado e cidade para consultar as unidades disponíveis.
              </EmptyState>
            ) : (
              <>
                <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
                  {/* aria-live: quem usa leitor de tela nao ve a lista mudar —
                      precisa ouvir quantas unidades a busca trouxe. */}
                  <p className="text-sm font-medium text-verde-profundo/70" aria-live="polite">
                    <ResultSummary
                      count={units.length}
                      state={search.filters.state}
                      city={search.filters.city}
                    />
                  </p>

                  <button
                    type="button"
                    onClick={search.clear}
                    className="text-xs text-dourado-escuro font-medium hover:text-verde-profundo transition-colors cursor-pointer"
                  >
                    Limpar busca
                  </button>
                </div>

                {units.length > 0 ? (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {units.map((unit) => (
                      <NetworkUnitCard key={unit.id} unit={unit} />
                    ))}
                  </div>
                ) : (
                  <EmptyState icon={<MapPinLg />} boxed>
                    Ainda não temos cobertura nessa região. Estamos em expansão — entre em contato para mais
                    informações.
                  </EmptyState>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Mapa ────────────────────────────────────────────────────────── */}
        <div className="lg:sticky lg:top-8">
          <p className="text-xs font-semibold text-verde-profundo/40 tracking-widest uppercase mb-4 text-center">
            Cobertura atual
          </p>

          <CoverageMap activeStates={coveredStates.data ?? []} onStateClick={search.searchByState} />

          <p className="text-[11px] text-verde-profundo/35 text-center mt-3 leading-relaxed">
            Clique em um estado destacado para ver as unidades disponíveis
          </p>
        </div>
      </div>
    </Section>
  )
}
